import axios from 'axios';

// window.axios sends the Kourses bearer token, CSRF token and
// X-Requested-With on every request (see bootstrap.js) — none of that
// should ever reach a third-party host. The presign/commit/abandon calls
// below go to our own backend and use window.axios; the PUT to the signed
// url leaves this application, so it uses this header-less instance instead.
//
// axios.create() is NOT a clean slate: it deep-merges the shared
// axios.defaults object, and bootstrap.js mutates
// axios.defaults.headers.common in place to add those same headers — so
// without clearing it here, this "isolated" instance inherits them anyway.
// S3 rejects a presigned request that also carries an Authorization
// header with a 400, which is what led to finding this.
const s3 = axios.create();
s3.defaults.headers.common = {};

/**
 * Uploads a file directly to S3 via a short-lived presigned url: ask the
 * backend for a signed PUT (presign), upload the bytes straight to S3,
 * then ask the backend to validate and finalize the object (commit).
 *
 * Framework-agnostic — no Vue, no DOM. See _docs/presigned-uploads-prd.md.
 */
class PresignedUploader {
    constructor(config) {
        this.config = {...{
            presignUrl: null,
            commitUrl: null,
            abandonUrl: null,
            context: null,
        }, ...config};

        this.cancelSource = null;
        this.tmpKey = null;
    }

    /**
     * @param   {File}      file
     * @param   {Object}    options
     * @param   {Function}  [options.onProgress]  (bytesSent, bytesTotal) => void
     * @param   {Function}  [options.onSuccess]   (committed: {path, url, name, size, mime}) => void
     * @param   {Function}  [options.onError]     (message: string|null) => void — null means the caller should show a generic message
     *
     * @return  {Promise}
     */
    upload(file, options = {}) {
        this.cancelSource = axios.CancelToken.source();

        return window.axios.post(this.config.presignUrl, {
            file_name: file.name,
            size: file.size,
            context: this.config.context,
        }, {
            cancelToken: this.cancelSource.token,
        })
        .then(({data: presigned}) => {
            this.tmpKey = presigned.key;

            return s3.put(presigned.url, file, {
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    ...(presigned.headers || {}),
                },
                cancelToken: this.cancelSource.token,
                onUploadProgress: (event) => {
                    if (options.onProgress) {
                        options.onProgress(event.loaded, event.total || file.size);
                    }
                },
            });
        })
        .then(() => {
            // Some contexts (audio) skip commit entirely — the tmp key
            // itself is the result, and the caller's own save flow does
            // the server-side equivalent of what commit does for regular
            // uploads. See _docs/audio-lessons-spec.md §3.
            if ( ! this.config.commitUrl) {
                return {path: this.tmpKey, name: file.name, size: file.size};
            }

            return window.axios.post(this.config.commitUrl, {
                key: this.tmpKey,
                file_name: file.name,
                context: this.config.context,
            }, {
                cancelToken: this.cancelSource.token,
            }).then(({data: committed}) => committed);
        })
        .then((committed) => {
            this.tmpKey = null;

            if (options.onSuccess) {
                options.onSuccess(committed);
            }

            return committed;
        })
        .catch((error) => {
            if (axios.isCancel(error)) {
                return;
            }

            // Whatever step failed (PUT rejected, or commit rejected the
            // object), clean up the temporary key. Harmless if it was
            // never actually written — S3 lifecycle rules are the backstop
            // either way.
            this.abandonPendingUpload();

            if (options.onError) {
                options.onError(this.extractErrorMessage(error));
            }

            throw error;
        });
    }

    /**
     * Cancel an in-progress upload and clean up its temporary object.
     */
    cancel() {
        if (this.cancelSource) {
            this.cancelSource.cancel('Upload cancelled.');
        }

        this.abandonPendingUpload();
    }

    abandonPendingUpload() {
        if ( ! this.tmpKey) {
            return;
        }

        window.axios.post(this.config.abandonUrl, {key: this.tmpKey});

        this.tmpKey = null;
    }

    extractErrorMessage(error) {
        const response = error.response;

        if ( ! response || ! response.data) {
            return null;
        }

        if (response.data.errors) {
            const firstField = Object.values(response.data.errors)[0];

            if (firstField && firstField[0]) {
                return firstField[0];
            }
        }

        return response.data.message || null;
    }
}

export default PresignedUploader;
